import {menuContentType} from "../../../constants/menuContentType"
import {ModesVisualisation} from "../../../constants/modesVisualisation"
import {getActivatingPages, isFeatureEnabled} from "../../../utils/sideMenuUtil"
import {ACTIVATING_PAGES} from "./activatingPages"
import {PageCESU, PageSAP} from "./pagesEBU"

/**
 * Retourne la liste d'items du menu en fonction du mode de visualisation: EBU (CESU + SAP) ou CESU SOCIAL ou CESU RH
 */
export const menuContent = ({
    features,
    fromBackOffice,
    hasContratSAP,
    isContractWithHoldingAllowed,
    isEbUnique,
    pagesArchitecture,
    readOnly,
    texts,
    visualisationMode,
    isSapOnly,
    certificatAidantIsActivated,
}) => {
    // Items communs du menu
    const homepageItem = {
        type: menuContentType.SIMPLE,
        page: PageCESU.HOME,
        icon: "fa-regular fa-house",
        activeIcon: "fa-solid fa-house",
    }

    const paymentChoiceItem = {
        type: menuContentType.SIMPLE,
        page: PageCESU.PAYMENT_CHOICE,
        activeOnPages: getActivatingPages(PageCESU.PAYMENT_CHOICE, ACTIVATING_PAGES),
    }

    const declarationHistoryItem = {
        type: menuContentType.SIMPLE,
        page: PageCESU.DECLARATION_HISTORY,
        activeOnPages: getActivatingPages(PageCESU.DECLARATION_HISTORY, ACTIVATING_PAGES),
    }

    const taxDocumentItem = {
        type: menuContentType.SIMPLE,
        page: PageCESU.TAX_DOCUMENT,
        activeOnPages: getActivatingPages(PageCESU.TAX_DOCUMENT, ACTIVATING_PAGES),
        hidden: !isFeatureEnabled(PageCESU.TAX_DOCUMENT, readOnly, features, pagesArchitecture),
    }

    const certificatAidantItem = {
        type: menuContentType.SIMPLE,
        page: PageCESU.CERTIFICAT_AIDANT,
        activeOnPages: getActivatingPages(PageCESU.CERTIFICAT_AIDANT, ACTIVATING_PAGES),
        hidden: !certificatAidantIsActivated,
    }

    // Items communs du menu
    // Si aucun contrat SAP n'est rattaché au contrat sélectionné, l'option "Commander une prestation"
    // (côté Domiserve) n'a pas de sens : on saute la modale de choix et on va direct à la recherche.
    const rechercheIntervenantItem = {
        type: menuContentType.SIMPLE,
        page: hasContratSAP ? PageCESU.INTERVENANT_SEARCH : PageCESU.INTERVENANT_DIRECT_SEARCH,
        icon: "fa-regular fa-magnifying-glass",
        activeIcon: "fa-solid fa-magnifying-glass",
        // Dispositif CESU uniquement : un bénéficiaire SAP only doit rester sur le parcours de commande.
        hidden: isSapOnly,
    }

    // MENU EBU & CESU RH
    // menu EBU = menu cesu rh + pages SAP accessibles depuis l'EBU
    if (isEbUnique || visualisationMode === ModesVisualisation.CESU_RH) {
        return [
            // -- Accueil --
            homepageItem,

            // -- Mes CESU --
            {
                type: menuContentType.ACCORDION,
                icon: "fa-kit fa-cesu-regular",
                hidden: isSapOnly,
                name: texts.menuCesuTitre,
                page: PageCESU.ORDER_SESSION_LIST,
                subItems: [
                    // -- Mes CESU/1/Commander mes CESU --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageCESU.ORDER_SESSION_LIST,
                        activeOnPages: getActivatingPages(PageCESU.ORDER_SESSION_LIST, ACTIVATING_PAGES),
                        hidden:
                            fromBackOffice ||
                            !isFeatureEnabled(PageCESU.ORDER_SESSION_LIST, readOnly, features, pagesArchitecture),
                    },
                    // -- Mes CESU/2/Mon solde --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageCESU.AMOUNT_STATEMENT,
                        activeOnPages: getActivatingPages(PageCESU.AMOUNT_STATEMENT, ACTIVATING_PAGES),
                        hidden:
                            fromBackOffice ||
                            !isFeatureEnabled(PageCESU.AMOUNT_STATEMENT, readOnly, features, pagesArchitecture),
                    },
                    // -- Mes CESU/3/Historique des commandes --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageCESU.ORDER_HISTORY,
                        activeOnPages: getActivatingPages(PageCESU.ORDER_HISTORY, ACTIVATING_PAGES),
                        hidden:
                            fromBackOffice ||
                            !isFeatureEnabled(PageCESU.ORDER_HISTORY, readOnly, features, pagesArchitecture),
                    },
                    // -- Mes CESU/4/Payer mon intervenant --
                    {
                        ...paymentChoiceItem,
                        hidden:
                            fromBackOffice ||
                            (!isFeatureEnabled(
                                PageCESU.ACCOUNT_PAYMENT_CHOICE,
                                readOnly,
                                features,
                                pagesArchitecture,
                            ) &&
                                !isFeatureEnabled(
                                    PageCESU.CHECK_PAYMENT_CHOICE,
                                    readOnly,
                                    features,
                                    pagesArchitecture,
                                )),
                    },
                    // -- Mes CESU/5/Historique des paiements --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageCESU.PAYMENT_HISTORY,
                        activeOnPages: getActivatingPages(PageCESU.PAYMENT_HISTORY, ACTIVATING_PAGES),
                        hidden:
                            fromBackOffice ||
                            !isFeatureEnabled(PageCESU.PAYMENT_HISTORY, readOnly, features, pagesArchitecture),
                    },
                    // -- Mes CESU/6/Historique des déclarations --
                    {
                        ...declarationHistoryItem,
                        hidden:
                            fromBackOffice ||
                            !isContractWithHoldingAllowed ||
                            !isFeatureEnabled(PageCESU.DECLARATION_HISTORY, readOnly, features, pagesArchitecture),
                    },
                    // -- Mes CESU/7/Changer de format --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageCESU.CESU_CONVERSION_CHOICE,
                        activeOnPages: getActivatingPages(PageCESU.CESU_CONVERSION_CHOICE, ACTIVATING_PAGES),
                        hidden:
                            fromBackOffice ||
                            !isFeatureEnabled(PageCESU.CESU_CONVERSION_CHOICE, readOnly, features, pagesArchitecture),
                    },
                    // -- Mes CESU/8/Echanger mes CESU --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageCESU.MILLESIME_TRADE_CHOICE,
                        activeOnPages: getActivatingPages(PageCESU.MILLESIME_TRADE_CHOICE, ACTIVATING_PAGES),
                        hidden: !isFeatureEnabled(
                            PageCESU.MILLESIME_TRADE_CHOICE,
                            readOnly,
                            features,
                            pagesArchitecture,
                        ),
                    },
                ],
                activeOnPages: [
                    ...getActivatingPages(PageCESU.ORDER_SESSION_LIST, ACTIVATING_PAGES),
                    ...getActivatingPages(PageCESU.AMOUNT_STATEMENT, ACTIVATING_PAGES),
                    ...getActivatingPages(PageCESU.ORDER_HISTORY, ACTIVATING_PAGES),
                    ...getActivatingPages(PageCESU.PAYMENT_CHOICE, ACTIVATING_PAGES),
                    ...getActivatingPages(PageCESU.PAYMENT_HISTORY, ACTIVATING_PAGES),
                    ...getActivatingPages(PageCESU.DECLARATION_HISTORY, ACTIVATING_PAGES),
                    ...getActivatingPages(PageCESU.CESU_CONVERSION_CHOICE, ACTIVATING_PAGES),
                    ...getActivatingPages(PageCESU.MILLESIME_TRADE_CHOICE, ACTIVATING_PAGES),
                ],
            },

            // -- Mes prestations de service --
            {
                type: menuContentType.ACCORDION,
                icon: "fa-regular fa-person-shelter",
                name: texts.menuPrestationsTitre,
                page: PageSAP.CREATION_DEMANDE_CHOIX_DISPOSITIF,
                subItems: [
                    // -- Commander une prestation --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageSAP.CREATION_DEMANDE_CHOIX_DISPOSITIF,
                        activeOnPages: getActivatingPages(PageSAP.CREATION_DEMANDE_CHOIX_DISPOSITIF, ACTIVATING_PAGES),
                    },
                    // -- Historique des commandes --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageSAP.COMMANDE_SUIVI,
                        activeOnPages: getActivatingPages(PageSAP.COMMANDE_SUIVI, ACTIVATING_PAGES),
                    },
                    // -- Régler mes factures --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageSAP.PAIEMENT_FACTURES,
                        activeOnPages: getActivatingPages(PageSAP.PAIEMENT_FACTURES, ACTIVATING_PAGES),
                    },
                    // -- Historique des factures --
                    {
                        type: menuContentType.SIMPLE,
                        page: PageSAP.FACTURES,
                        activeOnPages: getActivatingPages(PageSAP.FACTURES, ACTIVATING_PAGES),
                    },
                ],
                activeOnPages: [
                    ...getActivatingPages(PageSAP.CREATION_DEMANDE_CHOIX_DISPOSITIF, ACTIVATING_PAGES),
                    ...getActivatingPages(PageSAP.COMMANDE_SUIVI, ACTIVATING_PAGES),
                    ...getActivatingPages(PageSAP.PAIEMENT_FACTURES, ACTIVATING_PAGES),
                    ...getActivatingPages(PageSAP.FACTURES, ACTIVATING_PAGES),
                ],
                hidden: !isEbUnique, // Affiché seulement si EBU
            },

            // -- Mes documents --
            {
                type: menuContentType.ACCORDION,
                icon: "fa-regular fa-file",
                name: texts.menuDocumentsTitre,
                page: PageCESU.TAX_DOCUMENT,
                subItems: [taxDocumentItem],
                activeOnPages: getActivatingPages(PageCESU.TAX_DOCUMENT, ACTIVATING_PAGES),
            },
            {
                type: menuContentType.ACCORDION,
                icon: "fa-regular fa-file",
                name: texts.menuDocumentsTitre,
                page: PageCESU.CERTIFICAT_AIDANT,
                subItems: [certificatAidantItem],
                activeOnPages: getActivatingPages(PageCESU.CERTIFICAT_AIDANT, ACTIVATING_PAGES),
                hidden: !certificatAidantIsActivated,
            },

            //  modale recherche intervenant
            rechercheIntervenantItem,
        ]
    }

    // MENU EN MODE CESU SOCIAL
    if (visualisationMode === ModesVisualisation.CESU_SOCIAL) {
        return [
            // -- Accueil --
            homepageItem,

            // -- Payer mon intervenant --
            {
                ...paymentChoiceItem,
                icon: "fa-regular fa-euro-sign",
                activeIcon: "fa-solid fa-euro-sign",
                hidden: !isFeatureEnabled(PageCESU.PAYMENT_CHOICE, readOnly, features, pagesArchitecture),
            },

            // -- Historique des versements (= Mon solde) --
            {
                page: PageCESU.TRANSACTIONS_HISTORY,
                type: menuContentType.SIMPLE,
                icon: "fa-regular fa-timer",
                activeOnPages: getActivatingPages(PageCESU.TRANSACTIONS_HISTORY, ACTIVATING_PAGES),
                hidden: !isFeatureEnabled(PageCESU.TRANSACTIONS_HISTORY, readOnly, features, pagesArchitecture),
            },

            // -- Historique des déclarations --
            {
                ...declarationHistoryItem,
                icon: "fa-regular fa-timer",
                hidden:
                    !isContractWithHoldingAllowed ||
                    !isFeatureEnabled(PageCESU.DECLARATION_HISTORY, readOnly, features, pagesArchitecture),
            },

            // -- Mes documents --
            {
                type: menuContentType.ACCORDION,
                icon: "fa-regular fa-file",
                activeIcon: "fa-solid fa-file",
                name: texts.menuDocumentsTitre,
                page: PageCESU.TAX_DOCUMENT,
                subItems: [taxDocumentItem],
                activeOnPages: getActivatingPages(PageCESU.TAX_DOCUMENT, ACTIVATING_PAGES),
            },
        ]
    }
}

/**
 * Factory method to create a DropDown
 * @param dropdownText Text to show on the DropDown
 * @param isUserConnected show the drop down only when the user is connected
 * @param dropdownChoices all drop down choices
 * @param alignment LEFT or RIGHT
 */
export const createDropdown = (dropdownText, isUserConnected, dropdownChoices, alignment) => {
    return {
        type: "DROPDOWN",
        text: dropdownText,
        userConnected: isUserConnected,
        choices: dropdownChoices,
        alignment,
    }
}

/**
 * Factory method to create a menu item that opens a new web page.
 * @param actualPage page where the user is
 * @param navigate    navigate function for navigation
 * @param features   enabled application features
 * @param page       page where the item should redirect
 * @param pagesArchitecture the application pages architecture
 * @param text       override the text
 * @param isFeatureEnabled    override isFeatureEnabled
 */
export const createLink = (actualPage, navigate, features, page, pagesArchitecture, text, isFeatureEnabled) => {
    // Fetch configuration for the page from pagesArchitecture
    const pageConfig = pagesArchitecture[page]

    // Prepare the onClick handler that opens the link in a new tab
    const handleLinkClick = event => {
        window.open(pageConfig.route, "_blank", "noopener,noreferrer")
    }

    return {
        type: "ITEM",
        text: text || pageConfig.breadcrumbText, // Use custom text or fallback to breadcrumb text
        active: actualPage === page,
        onClick: handleLinkClick, // Use the custom handleLinkClick function
        userConnected: pageConfig.userConnected,
        permissions: pageConfig.permissions,
        isFeatureEnabled:
            !pageConfig.featureFlag || (pageConfig.featureFlag && features.includes(pageConfig.featureFlag)),
        feature: pageConfig.featureFlag,
    }
}

/* Factory method to create a menu item
 * @param actualPage page where the user is
 * @param navigate    navigate function for navigation
 * @param features   enabled application features
 * @param page       page where the item should redirect
 * @param pagesArchitecture the application pages architecture
 * @param text       override the text
 * @param onClick    override the onClick function
 * @param isFeatureEnabled    override isFeatureEnabled
 */
export const createItem = (
    actualPage,
    navigate,
    features,
    page,
    pagesArchitecture,
    text,
    onClick,
    isFeatureEnabled,
) => ({
    type: "ITEM",
    text: text ? text : pagesArchitecture[page].breadcrumbText,
    active: actualPage === page,
    onClick: onClick ? () => onClick() : () => navigate(pagesArchitecture[page].route),
    userConnected: pagesArchitecture[page].userConnected,
    permissions: pagesArchitecture[page].permissions,
    isFeatureEnabled: isFeatureEnabled
        ? isFeatureEnabled
        : !pagesArchitecture[page].featureFlag ||
          (pagesArchitecture[page].featureFlag && features.includes(pagesArchitecture[page].featureFlag)),
    feature: pagesArchitecture[page].featureFlag,
})
